import React, { useEffect, useState } from "react";
import { CodeIcon } from "@heroicons/react/solid";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import ProjectModal from "../modals/ProjectModal";
import projects from "../data/data.json";

function ShowCaseProjects() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const controls = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView) controls.start({ opacity: 1, y: 0 });
  }, [controls, inView]);

  const openModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedProject(null);
    setIsModalOpen(false);
  };

  return (
    <section id="projects" className="py-20 bg-gray-900">
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex flex-col items-center mb-16">
          <CodeIcon className="w-8 h-8 text-emerald-400 mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Selected Projects</h2>
          <div className="section-divider" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" ref={ref}>
          {projects.map((project, i) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 30 }}
              animate={controls}
              transition={{ delay: i * 0.08 }}
              onClick={() => openModal(project)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openModal(project)}
              role="button"
              tabIndex={0}
              aria-label={`View details for ${project.title}`}
              className="group bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden cursor-pointer hover:border-emerald-500/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-500/10 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            >
              <div className="relative overflow-hidden aspect-video">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-60" />
              </div>
              <div className="p-5">
                <h3 className="text-white font-semibold text-lg mb-1">{project.title}</h3>
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.Stack.slice(0, 3).map((stack) => (
                    <span
                      key={stack}
                      className="text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full"
                    >
                      {stack}
                    </span>
                  ))}
                  {project.Stack.length > 3 && (
                    <span className="text-xs text-gray-500 px-2 py-0.5">
                      +{project.Stack.length - 3} more
                    </span>
                  )}
                </div>
                <button className="text-emerald-400 text-sm font-medium flex items-center gap-2 hover:text-emerald-300 transition-colors">
                  View details <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {isModalOpen && selectedProject && (
        <ProjectModal project={selectedProject} onClose={closeModal} />
      )}
    </section>
  );
}

export default ShowCaseProjects;
